// Global (user-scoped) settings page.
//
// Today: just the GitHub App connection. Lives at /account/settings —
// sibling to /account/usage. Adding new sections (Notifications, API
// keys, etc.) is one GlassPanel per concern.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Bot,
	CheckCircle2,
	ExternalLink,
	Github,
	Hash,
	Loader2,
	Mail,
	Pause,
	Pencil,
	Pin,
	Plus,
	RotateCcw,
	Slack,
	Trash2,
	Users,
	X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { GlassPanel, Pill } from "@/components/glass";
import { toast } from "@/components/toast";
import { authClient, useSession } from "@/lib/auth-client";

interface AppInfo {
	slug: string;
	name: string;
	html_url: string;
	install_url: string;
}

interface GithubInstallation {
	id: string;
	installationId: number;
	accountLogin: string;
	accountType: string; // "User" | "Organization"
	repositorySelection: string | null; // "all" | "selected"
	suspendedAt: number | null;
	createdAt: number;
}

interface Project {
	id: string;
	name: string;
	organizationId?: string | null;
}

interface Workspace {
	id: string;
	name: string;
	slug: string;
	role: string;
	createdAt: number;
}

interface WorkspaceMember {
	id: string;
	role: string;
	createdAt: number;
	user: {
		id: string;
		name: string;
		email: string;
		image: string | null;
	} | null;
}

interface SettingsPayload {
	pinnedProjectIds: string[];
}

async function fetchAppInfo(): Promise<AppInfo> {
	const r = await fetch("/api/github/app", { credentials: "include" });
	if (!r.ok) throw new Error(`github app info: ${r.status}`);
	return r.json();
}

async function fetchInstallations(): Promise<GithubInstallation[]> {
	const r = await fetch("/api/github/installations", {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`github installations: ${r.status}`);
	const body = (await r.json()) as { installations: GithubInstallation[] };
	return body.installations;
}

async function fetchProjects(): Promise<Project[]> {
	const r = await fetch("/api/projects", { credentials: "include" });
	if (!r.ok) throw new Error(`projects: ${r.status}`);
	return ((await r.json()) as { projects: Project[] }).projects;
}

async function fetchSettings(): Promise<SettingsPayload> {
	const r = await fetch("/api/settings", { credentials: "include" });
	if (!r.ok) throw new Error(`settings: ${r.status}`);
	return ((await r.json()) as { settings: SettingsPayload }).settings;
}

async function fetchWorkspaces(): Promise<Workspace[]> {
	const r = await fetch("/api/workspaces", { credentials: "include" });
	if (!r.ok) throw new Error(`workspaces: ${r.status}`);
	return ((await r.json()) as { workspaces: Workspace[] }).workspaces;
}

async function fetchWorkspaceMembers(
	workspaceId: string,
): Promise<WorkspaceMember[]> {
	const r = await fetch(`/api/workspaces/${workspaceId}/members`, {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`workspace members: ${r.status}`);
	return ((await r.json()) as { members: WorkspaceMember[] }).members;
}

export default function AccountSettings() {
	return (
		<div className="h-full overflow-y-auto pt-1 pb-8">
			<div className="max-w-[800px] mx-auto space-y-4">
				<h1 className="text-[18px] font-medium tracking-tight mt-2 mb-4">
					Account settings
				</h1>

				<WorkspaceSection />
				<PinnedProjectsSection />
				<GithubSection />
				<GmailSection />
				<IntegrationsSection />
				<DebugSection />
			</div>
		</div>
	);
}

function WorkspaceSection() {
	const qc = useQueryClient();
	const session = useSession();
	const workspacesQ = useQuery({
		queryKey: ["workspaces"],
		queryFn: fetchWorkspaces,
	});
	const activeWorkspaceId = (
		session.data?.session as { activeOrganizationId?: string | null } | null
	)?.activeOrganizationId;
	const workspaces = workspacesQ.data ?? [];
	const activeWorkspace =
		workspaces.find((workspace) => workspace.id === activeWorkspaceId) ??
		workspaces[0] ??
		null;
	const membersQ = useQuery({
		queryKey: ["workspace-members", activeWorkspace?.id],
		queryFn: () => fetchWorkspaceMembers(activeWorkspace!.id),
		enabled: !!activeWorkspace?.id,
	});
	const [newName, setNewName] = useState("");
	const [renameValue, setRenameValue] = useState("");

	useEffect(() => {
		setRenameValue(activeWorkspace?.name ?? "");
	}, [activeWorkspace?.id, activeWorkspace?.name]);

	const createWorkspace = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/workspaces", {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name: newName.trim() }),
			});
			if (!r.ok) throw new Error(`workspace create: ${r.status}`);
		},
		onSuccess: async () => {
			setNewName("");
			await qc.invalidateQueries({ queryKey: ["workspaces"] });
			await qc.invalidateQueries({ queryKey: ["organizations"] });
			await qc.invalidateQueries({ queryKey: ["projects"] });
			await qc.invalidateQueries({ queryKey: ["session"] });
			await authClient.getSession().catch(() => {});
			toast.success("Workspace created");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't create workspace" }),
	});

	const renameWorkspace = useMutation({
		mutationFn: async () => {
			if (!activeWorkspace) throw new Error("No workspace selected");
			const r = await fetch(`/api/workspaces/${activeWorkspace.id}`, {
				method: "PATCH",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name: renameValue.trim() }),
			});
			if (!r.ok) throw new Error(`workspace rename: ${r.status}`);
		},
		onSuccess: async () => {
			await qc.invalidateQueries({ queryKey: ["workspaces"] });
			await qc.invalidateQueries({ queryKey: ["organizations"] });
			toast.success("Workspace renamed");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't rename workspace" }),
	});

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start gap-3">
				<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					<Users className="w-4 h-4 text-[var(--color-text)]" />
				</div>
				<div>
					<div className="text-[14px] font-medium">Workspaces</div>
					<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
						Create and manage workspaces after onboarding. New projects are
						created in the active workspace.
					</div>
				</div>
			</div>

			<div className="grid gap-3 md:grid-cols-[minmax(0,220px)_1fr]">
				<div className="space-y-2">
					{workspaces.map((workspace) => {
						const active = workspace.id === activeWorkspace?.id;
						return (
							<button
								key={workspace.id}
								type="button"
								onClick={async () => {
									await authClient.organization.setActive({
										organizationId: workspace.id,
									});
									await qc.invalidateQueries({ queryKey: ["session"] });
									await qc.invalidateQueries({ queryKey: ["organizations"] });
									await qc.invalidateQueries({ queryKey: ["projects"] });
									await authClient.getSession().catch(() => {});
								}}
								className={`w-full text-left px-3 py-2.5 rounded-[10px] border transition-colors ${
									active
										? "border-[var(--color-border-hi)] bg-white/[0.05]"
										: "border-[var(--color-border)] bg-white/[0.02] hover:bg-white/[0.04]"
								}`}
							>
								<div className="text-[13px]">{workspace.name}</div>
								<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
									{workspace.role}
									{active ? " · active" : ""}
								</div>
							</button>
						);
					})}
				</div>

				<div className="space-y-4">
					<div className="space-y-2">
						<label className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
							Create workspace
						</label>
						<div className="flex gap-2">
							<input
								type="text"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								placeholder="Northstar"
								className="flex-1 h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
							/>
							<Pill
								onClick={() => createWorkspace.mutate()}
								disabled={!newName.trim() || createWorkspace.isPending}
							>
								{createWorkspace.isPending ? (
									<Loader2 className="w-3.5 h-3.5 animate-spin" />
								) : (
									"Create"
								)}
							</Pill>
						</div>
					</div>

					{activeWorkspace && (
						<div className="space-y-3">
							<div className="space-y-2">
								<label className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
									Rename active workspace
								</label>
								<div className="flex gap-2">
									<input
										type="text"
										value={renameValue}
										onChange={(e) => setRenameValue(e.target.value)}
										className="flex-1 h-9 px-3 rounded-[var(--radius-pill)] bg-[var(--color-surface-solid)] border border-[var(--color-border)] text-[13px] outline-none focus:border-[var(--color-border-hi)]"
									/>
									<Pill
										onClick={() => renameWorkspace.mutate()}
										disabled={
											!renameValue.trim() ||
											renameValue.trim() === activeWorkspace.name ||
											renameWorkspace.isPending
										}
									>
										{renameWorkspace.isPending ? (
											<Loader2 className="w-3.5 h-3.5 animate-spin" />
										) : (
											<Pencil className="w-3.5 h-3.5" />
										)}
										Save
									</Pill>
								</div>
							</div>

							<div className="rounded-[10px] border border-[var(--color-border)] bg-white/[0.02] p-3">
								<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] mb-2">
									Members
								</div>
								<div className="space-y-2">
									{(membersQ.data ?? []).map((member) => (
										<div
											key={member.id}
											className="flex items-center justify-between gap-3 text-[12px]"
										>
											<div className="min-w-0">
												<div className="truncate">
													{member.user?.name ?? "Unknown user"}
												</div>
												<div className="truncate text-[var(--color-text-dim)]">
													{member.user?.email ?? "—"}
												</div>
											</div>
											<div className="text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider">
												{member.role}
											</div>
										</div>
									))}
									{membersQ.isLoading && (
										<div className="text-[12px] text-[var(--color-text-muted)]">
											Loading members…
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</GlassPanel>
	);
}

function PinnedProjectsSection() {
	const qc = useQueryClient();
	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
	});
	const settingsQ = useQuery({
		queryKey: ["settings"],
		queryFn: fetchSettings,
	});
	const pinnedIds = settingsQ.data?.pinnedProjectIds ?? [];
	const pinnedSet = new Set(pinnedIds);

	const savePinned = useMutation({
		mutationFn: async (pinnedProjectIds: string[]) => {
			const r = await fetch("/api/settings", {
				method: "PUT",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ pinnedProjectIds }),
			});
			if (!r.ok) throw new Error(`pin project: ${r.status}`);
		},
		onSuccess: async () => {
			await qc.invalidateQueries({ queryKey: ["settings"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't update pinned projects" }),
	});

	const togglePin = (projectId: string) => {
		const next = pinnedSet.has(projectId)
			? pinnedIds.filter((id) => id !== projectId)
			: [...pinnedIds, projectId];
		savePinned.mutate(next);
	};

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start gap-3">
				<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					<Pin className="w-4 h-4 text-[var(--color-text)]" />
				</div>
				<div>
					<div className="text-[14px] font-medium">Pinned projects</div>
					<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
						Pinned projects float to the top of the project picker. Pin as many
						as you like.
					</div>
				</div>
			</div>

			<div className="space-y-2">
				{(projectsQ.data ?? []).map((project) => {
					const pinned = pinnedSet.has(project.id);
					return (
						<button
							key={project.id}
							type="button"
							onClick={() => togglePin(project.id)}
							className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-[10px] border transition-colors ${
								pinned
									? "border-[var(--color-border-hi)] bg-white/[0.05]"
									: "border-[var(--color-border)] bg-white/[0.02] hover:bg-white/[0.04]"
							}`}
						>
							<div className="text-left">
								<div className="text-[13px]">{project.name}</div>
							</div>
							<div
								className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider ${
									pinned ? "text-amber-300" : "text-[var(--color-text-dim)]"
								}`}
							>
								<Pin
									className={`w-3.5 h-3.5 ${pinned ? "fill-current" : ""}`}
								/>
								{pinned ? "Pinned" : "Pin"}
							</div>
						</button>
					);
				})}
			</div>
		</GlassPanel>
	);
}

function GithubSection() {
	const qc = useQueryClient();
	const appQ = useQuery({
		queryKey: ["github-app"],
		queryFn: fetchAppInfo,
		staleTime: 5 * 60 * 1000, // app info doesn't change
	});
	const installsQ = useQuery({
		queryKey: ["github-installations"],
		queryFn: fetchInstallations,
		// Refetch on focus — when the user returns from the GitHub install
		// page, our webhook has just landed and the new install should appear.
		refetchOnWindowFocus: true,
	});

	const installs = installsQ.data ?? [];
	const hasInstalls = installs.length > 0;

	function openInstall() {
		if (!appQ.data) return;
		window.open(appQ.data.install_url, "_blank", "noopener,noreferrer");
		// Re-poll a few times in the background to catch the webhook landing.
		const start = Date.now();
		const interval = setInterval(() => {
			qc.invalidateQueries({ queryKey: ["github-installations"] });
			if (Date.now() - start > 60_000) clearInterval(interval);
		}, 3_000);
	}

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<Github className="w-4 h-4 text-[var(--color-text)]" />
					</div>
					<div>
						<div className="text-[14px] font-medium">GitHub</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Connect your GitHub account so projects can link to repositories.
						</div>
					</div>
				</div>
				<Pill onClick={openInstall} disabled={!appQ.data}>
					<ExternalLink className="w-3.5 h-3.5" />
					{hasInstalls ? "Install on another account" : "Install GitHub App"}
				</Pill>
			</div>

			{installsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : installsQ.error ? (
				<div className="text-[13px] text-[var(--color-danger)] py-4 text-center border border-dashed border-[rgba(239,68,68,0.35)] rounded-[8px]">
					Could not load installations. {(installsQ.error as Error).message}
				</div>
			) : hasInstalls ? (
				<div className="space-y-2 pt-1">
					{installs.map((inst) => (
						<InstallationRow key={inst.id} install={inst} />
					))}
				</div>
			) : (
				<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					No GitHub accounts connected yet. Install the app on a user or
					organization to get started.
				</div>
			)}
		</GlassPanel>
	);
}

function InstallationRow({ install }: { install: GithubInstallation }) {
	const suspended = install.suspendedAt != null;
	const repoMode =
		install.repositorySelection === "all" ? "all repos" : "selected repos";

	return (
		<div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02]">
			<div className="flex items-center gap-3 min-w-0">
				<div className="w-6 h-6 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center shrink-0">
					{install.accountType === "Organization" ? (
						<Github className="w-3 h-3" />
					) : (
						<span className="text-[10px] mono">
							{install.accountLogin[0]?.toUpperCase()}
						</span>
					)}
				</div>
				<div className="min-w-0">
					<div className="text-[13px] truncate">{install.accountLogin}</div>
					<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5">
						{install.accountType.toLowerCase()} · {repoMode}
					</div>
				</div>
			</div>
			<div className="flex items-center gap-1.5 text-[11px]">
				{suspended ? (
					<>
						<Pause className="w-3 h-3 text-[var(--color-text-muted)]" />
						<span className="text-[var(--color-text-muted)]">suspended</span>
					</>
				) : (
					<>
						<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
						<span className="text-[var(--color-text-muted)]">active</span>
					</>
				)}
			</div>
		</div>
	);
}

interface GmailAccount {
	id: string;
	email: string;
	createdAt: number;
	updatedAt: number;
	revokedAt: number | null;
}

async function fetchGmailAccounts(): Promise<GmailAccount[]> {
	const r = await fetch("/api/gmail/accounts", { credentials: "include" });
	if (!r.ok) throw new Error(`gmail accounts: ${r.status}`);
	const body = (await r.json()) as { accounts: GmailAccount[] };
	return body.accounts;
}

function GmailSection() {
	const qc = useQueryClient();
	const accountsQ = useQuery({
		queryKey: ["gmail-accounts"],
		queryFn: fetchGmailAccounts,
		refetchOnWindowFocus: true,
	});

	const accounts = accountsQ.data ?? [];
	const hasAccount = accounts.some((a) => !a.revokedAt);

	const connect = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/gmail/connect", { credentials: "include" });
			if (!r.ok) throw new Error(`gmail connect: ${r.status}`);
			const body = (await r.json()) as { url?: string };
			if (!body.url) throw new Error("no oauth url returned");
			window.open(body.url, "_blank", "noopener,noreferrer");
			// Re-poll a few times so the new account row shows up
			// after the redirect-back.
			const start = Date.now();
			const interval = setInterval(() => {
				qc.invalidateQueries({ queryKey: ["gmail-accounts"] });
				if (Date.now() - start > 60_000) clearInterval(interval);
			}, 3_000);
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't open Gmail consent screen" }),
	});

	const disconnect = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/gmail/accounts/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`gmail disconnect: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Gmail disconnected");
			qc.invalidateQueries({ queryKey: ["gmail-accounts"] });
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't disconnect Gmail" }),
	});

	const resync = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/gmail/accounts/${id}/baseline-sync`, {
				method: "POST",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`gmail resync: ${r.status}`);
		},
		onSuccess: () => toast.success("Baseline sync started"),
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't start resync" }),
	});

	return (
		<GlassPanel className="p-5 space-y-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						<Mail className="w-4 h-4 text-[var(--color-text)]" />
					</div>
					<div>
						<div className="text-[14px] font-medium">Gmail</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							Read-only access to your inbox. Ingests message metadata (headers
							+ snippet) — not bodies.
						</div>
					</div>
				</div>
				<Pill onClick={() => connect.mutate()} disabled={connect.isPending}>
					{connect.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<ExternalLink className="w-3.5 h-3.5" />
					)}
					{hasAccount ? "Reconnect" : "Connect Gmail"}
				</Pill>
			</div>

			{accountsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : accountsQ.error ? (
				<div className="text-[13px] text-[var(--color-danger)] py-4 text-center border border-dashed border-[rgba(239,68,68,0.35)] rounded-[8px]">
					Could not load Gmail accounts. {(accountsQ.error as Error).message}
				</div>
			) : accounts.length > 0 ? (
				<div className="space-y-2 pt-1">
					{accounts.map((acct) => {
						const revoked = acct.revokedAt != null;
						return (
							<div
								key={acct.id}
								className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-[8px] border border-[var(--color-border)] bg-white/[0.02]"
							>
								<div className="flex items-center gap-3 min-w-0">
									<div className="w-6 h-6 rounded-full bg-[var(--color-surface-solid)] border border-[var(--color-border)] flex items-center justify-center shrink-0">
										<Mail className="w-3 h-3" />
									</div>
									<div className="min-w-0">
										<div className="text-[13px] truncate">{acct.email}</div>
										<div className="text-[11px] text-[var(--color-text-dim)] mt-0.5 flex items-center gap-1.5">
											{revoked ? (
												<>
													<Pause className="w-3 h-3" />
													<span>token revoked</span>
												</>
											) : (
												<>
													<CheckCircle2 className="w-3 h-3 text-emerald-400/80" />
													<span>active</span>
												</>
											)}
										</div>
									</div>
								</div>
								<div className="flex items-center gap-1.5">
									<button
										type="button"
										onClick={() => resync.mutate(acct.id)}
										disabled={resync.isPending || revoked}
										className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40 inline-flex items-center gap-1"
									>
										<RotateCcw className="w-3 h-3" />
										Resync
									</button>
									<button
										type="button"
										onClick={() => disconnect.mutate(acct.id)}
										disabled={disconnect.isPending}
										className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 inline-flex items-center gap-1"
									>
										<Trash2 className="w-3 h-3" />
										Disconnect
									</button>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="text-[13px] text-[var(--color-text-muted)] py-4 text-center border border-dashed border-[var(--color-border)] rounded-[8px]">
					No Gmail account connected yet.
				</div>
			)}
		</GlassPanel>
	);
}

// Spec 45 — external agents. Connect Slack/Discord, choose the default project,
// and bind specific channels to specific projects.
interface ChannelBinding {
	id: string;
	channelId: string;
	channelLabel: string;
	projectId: string;
}
interface InstallView {
	id: string;
	workspaceName: string | null;
	defaultProjectId: string | null;
	channelBindings: ChannelBinding[];
}
type IntegrationsPayload = {
	slack: InstallView | null;
	discord: InstallView | null;
};

async function fetchIntegrations(): Promise<IntegrationsPayload> {
	const r = await fetch("/api/integrations/installs", {
		credentials: "include",
	});
	if (!r.ok) throw new Error(`integrations: ${r.status}`);
	return (await r.json()) as IntegrationsPayload;
}

function IntegrationsSection() {
	const integrationsQ = useQuery({
		queryKey: ["integrations"],
		queryFn: fetchIntegrations,
		refetchOnWindowFocus: true,
	});
	const projectsQ = useQuery({
		queryKey: ["projects"],
		queryFn: fetchProjects,
	});
	const projects = projectsQ.data ?? [];

	return (
		<GlassPanel className="p-5 space-y-4">
			<div>
				<div className="text-[14px] font-medium">Chat integrations</div>
				<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
					Let your team @-mention Renketsu in Slack or Discord and get answers
					from a project's graph, in-thread.
				</div>
			</div>

			{integrationsQ.isLoading ? (
				<div className="flex items-center gap-2 py-3 text-[13px] text-[var(--color-text-muted)]">
					<Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
				</div>
			) : (
				<div className="space-y-3">
					<ServiceIntegration
						service="slack"
						label="Slack"
						icon={<Slack className="w-4 h-4" />}
						description="Mention the bot in any channel it's in; it replies in the thread."
						install={integrationsQ.data?.slack ?? null}
						projects={projects}
					/>
					<ServiceIntegration
						service="discord"
						label="Discord"
						icon={<Bot className="w-4 h-4" />}
						description="Mention the bot in a server channel; it replies inline."
						install={integrationsQ.data?.discord ?? null}
						projects={projects}
					/>
				</div>
			)}
		</GlassPanel>
	);
}

function ServiceIntegration({
	service,
	label,
	icon,
	description,
	install,
	projects,
}: {
	service: "slack" | "discord";
	label: string;
	icon: React.ReactNode;
	description: string;
	install: InstallView | null;
	projects: Project[];
}) {
	const qc = useQueryClient();
	const [channelId, setChannelId] = useState("");
	const [bindProjectId, setBindProjectId] = useState("");

	const invalidate = () => qc.invalidateQueries({ queryKey: ["integrations"] });

	const connect = () => {
		window.open(
			`/api/integrations/${service}/install`,
			"_blank",
			"noopener,noreferrer",
		);
		// Re-poll so the new install shows up after the redirect-back.
		const start = Date.now();
		const interval = setInterval(() => {
			invalidate();
			if (Date.now() - start > 60_000) clearInterval(interval);
		}, 3_000);
	};

	const setDefault = useMutation({
		mutationFn: async (projectId: string | null) => {
			const r = await fetch("/api/integrations/default-project", {
				method: "PUT",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ service, projectId }),
			});
			if (!r.ok) throw new Error(`set default: ${r.status}`);
		},
		onSuccess: invalidate,
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't set default project" }),
	});

	const addBinding = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/integrations/channel-bindings", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({
					service,
					channelId: channelId.trim(),
					projectId: bindProjectId,
				}),
			});
			if (!r.ok) throw new Error(`add binding: ${r.status}`);
		},
		onSuccess: () => {
			setChannelId("");
			setBindProjectId("");
			invalidate();
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't add channel binding" }),
	});

	const removeBinding = useMutation({
		mutationFn: async (id: string) => {
			const r = await fetch(`/api/integrations/channel-bindings/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`remove binding: ${r.status}`);
		},
		onSuccess: invalidate,
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't remove binding" }),
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/integrations/disconnect", {
				method: "POST",
				credentials: "include",
				headers: { "content-type": "application/json" },
				body: JSON.stringify({ service }),
			});
			if (!r.ok) throw new Error(`disconnect: ${r.status}`);
		},
		onSuccess: () => {
			toast.success(`${label} disconnected`);
			invalidate();
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: `Couldn't disconnect ${label}` }),
	});

	return (
		<div className="rounded-[8px] border border-[var(--color-border)] bg-white/[0.02] p-3.5 space-y-3">
			<div className="flex items-start justify-between gap-4">
				<div className="flex items-start gap-3">
					<div className="w-8 h-8 rounded-[8px] bg-white/[0.04] border border-[var(--color-border)] flex items-center justify-center shrink-0">
						{icon}
					</div>
					<div>
						<div className="text-[13px] font-medium flex items-center gap-2">
							{label}
							{install && (
								<span className="text-[10px] text-emerald-400/80 inline-flex items-center gap-1">
									<CheckCircle2 className="w-3 h-3" />
									{install.workspaceName ?? "connected"}
								</span>
							)}
						</div>
						<div className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
							{description}
						</div>
					</div>
				</div>
				{install ? (
					<button
						type="button"
						onClick={() => disconnect.mutate()}
						disabled={disconnect.isPending}
						className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
					>
						<Trash2 className="w-3 h-3" />
						Disconnect
					</button>
				) : (
					<Pill onClick={connect}>
						<ExternalLink className="w-3.5 h-3.5" />
						Connect {label}
					</Pill>
				)}
			</div>

			{install && (
				<div className="space-y-3 pt-1 border-t border-[var(--color-border)]">
					{/* Default project */}
					<div className="flex items-center justify-between gap-3 pt-2">
						<div className="text-[12px] text-[var(--color-text-muted)]">
							Default project
						</div>
						<select
							value={install.defaultProjectId ?? ""}
							onChange={(e) =>
								setDefault.mutate(e.target.value === "" ? null : e.target.value)
							}
							className="text-[12px] bg-[var(--color-surface-solid)] border border-[var(--color-border)] rounded-[6px] px-2 py-1 max-w-[55%]"
						>
							<option value="">None</option>
							{projects.map((p) => (
								<option key={p.id} value={p.id}>
									{p.name}
								</option>
							))}
						</select>
					</div>

					{/* Channel bindings */}
					<div className="space-y-1.5">
						<div className="text-[11px] text-[var(--color-text-dim)] uppercase tracking-wider">
							Channel overrides
						</div>
						{install.channelBindings.map((b) => {
							const proj = projects.find((p) => p.id === b.projectId);
							return (
								<div
									key={b.id}
									className="flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded-[6px] bg-white/[0.02] border border-[var(--color-border)]"
								>
									<div className="flex items-center gap-1.5 min-w-0">
										<Hash className="w-3 h-3 text-[var(--color-text-dim)] shrink-0" />
										<span className="truncate">{b.channelLabel}</span>
										<span className="text-[var(--color-text-dim)]">→</span>
										<span className="truncate">
											{proj?.name ?? b.projectId}
										</span>
									</div>
									<button
										type="button"
										onClick={() => removeBinding.mutate(b.id)}
										disabled={removeBinding.isPending}
										className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] disabled:opacity-40 shrink-0"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
							);
						})}
						<div className="flex items-center gap-2 pt-1">
							<input
								value={channelId}
								onChange={(e) => setChannelId(e.target.value)}
								placeholder="channel id"
								className="text-[12px] bg-[var(--color-surface-solid)] border border-[var(--color-border)] rounded-[6px] px-2 py-1 flex-1 min-w-0"
							/>
							<select
								value={bindProjectId}
								onChange={(e) => setBindProjectId(e.target.value)}
								className="text-[12px] bg-[var(--color-surface-solid)] border border-[var(--color-border)] rounded-[6px] px-2 py-1"
							>
								<option value="">project…</option>
								{projects.map((p) => (
									<option key={p.id} value={p.id}>
										{p.name}
									</option>
								))}
							</select>
							<button
								type="button"
								onClick={() => addBinding.mutate()}
								disabled={
									!channelId.trim() || !bindProjectId || addBinding.isPending
								}
								className="text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40 inline-flex items-center gap-1 shrink-0"
							>
								<Plus className="w-3.5 h-3.5" />
								Add
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// Temporary surface for dogfood-phase testing. Remove (or move
// behind an admin-only gate) once onboarding is stable.
function DebugSection() {
	const navigate = useNavigate();
	const reset = useMutation({
		mutationFn: async () => {
			const r = await fetch("/api/onboarding/reset", {
				method: "POST",
				credentials: "include",
			});
			if (!r.ok) throw new Error(`reset: ${r.status}`);
		},
		onSuccess: () => {
			toast.success("Onboarding reset");
			navigate("/welcome");
		},
		onError: (e: Error) =>
			toast.error(e.message, { title: "Couldn't reset onboarding" }),
	});

	return (
		<GlassPanel className="p-5 space-y-3">
			<div>
				<div className="flex items-center gap-2">
					<div className="text-[13px] font-medium">Debug</div>
					<span className="text-[9px] uppercase tracking-wider text-[var(--color-text-dim)] border border-[var(--color-border)] px-1.5 py-0.5 rounded">
						temporary
					</span>
				</div>
				<div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
					Dogfood-phase shortcuts. These will be removed.
				</div>
			</div>

			<div className="flex items-center justify-between gap-3 pt-1 border-t border-[var(--color-border)]">
				<div>
					<div className="text-[12px]">Reset onboarding</div>
					<div className="text-[10px] text-[var(--color-text-dim)] mt-0.5">
						Wipes your onboarding state and sends you back to /welcome.
					</div>
				</div>
				<Pill onClick={() => reset.mutate()} disabled={reset.isPending}>
					{reset.isPending ? (
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
					) : (
						<RotateCcw className="w-3.5 h-3.5" />
					)}
					Reset
				</Pill>
			</div>
		</GlassPanel>
	);
}
